/**
 * 작성자 : 홍선영
 * 날짜 : 2024.08.14
 * 경로 : 사용자관리-사용자 정보/메뉴관리-CCTV모니터링관리(내부탭)
 */

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SelectBox from '../../../components/SelectBox';
import { useQuery } from '@tanstack/react-query';
import { IComCd } from 'customTypes';
import { IUser } from '../../../atoms';
import { apiGet, apiPatch } from '../../../services/_common';
import { COMCD_INFRA } from '../../../_constants';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import Input from '../../../components/Input';
import IssueGuide from '../../../components/IssueGuide';
import { PulseLoader } from 'react-spinners';
import { arraySortByAscdOrder } from '../../../utils/arraySortByAscdOrder';
import { BtnBlue, BtnGhost } from '../../../components/Button';
import { useSetAuth } from '../../../utils/useSetAuth';
import { ButtonsWrapper } from '../../sm_setting/setting7/cctvInfoStyle';
import { toast } from 'react-toastify';
import { InputTable } from '../../../assets/styles/InputTable';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex-grow: 1;
  .thead {
    position: sticky;
    top: 0;
  }
  .detailInfo {
    padding: 0.5rem;
    position: sticky;
    left: 0;
    .userSet-data {
      background-color: var(--tonal);
      height: 2.5rem;
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      padding: 0 0.5rem;
      color: var(--text-primary);
      outline: 1px solid var(--outline);
      border-radius: 0.25rem;
      span:nth-child(n + 2)::before {
        content: '•';
        margin: 0 0.25rem;
      }
    }
  }
  .fit-width {
    width: fit-content !important;
  }
`;

interface IProps {
  orgUserInfo: any;
  setOrgUserInfo: Dispatch<SetStateAction<any>>;
  userInfo: IUser;
}

const UserCctvSetting = ({ orgUserInfo, setOrgUserInfo, userInfo }: IProps) => {
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const [infraCdList, setInfraCdList] = useState([]);
  const [sInfraSearch, setSInfraSearch] = useState<any>({ type: COMCD_INFRA, [COMCD_INFRA]: 'A', cdName: t('전체') }); // 인프라 검색값
  const [searchOption, setSearchOption] = useState({ nName: '' });
  const {
    data: normalCd,
    isLoading: isLoadingNormalCdN,
    isFetching: isFetchingNormalCdN,
    isError: isErrorNormalCdN,
  } = useQuery([`normalCdNGet_${userInfo.hCd}_${userInfo.sCd}`, userInfo.hCd, userInfo.sCd], () => fetchNormalCdN(), {
    enabled: !!userInfo.hCd && (userInfo.sCd === '' || userInfo.sCd === '00000'),
  });

  const {
    data: nvrList,
    isLoading: isLoadingNvrList,
    isFetching: isFetchingNvrList,
    isError: isErrorNvrList,
  } = useQuery([`nvrGet${userInfo.hCd}`, userInfo.hCd], () => fetchNvrList(), {
    enabled: !!userInfo.hCd && (userInfo.sCd === '' || userInfo.sCd === '00000'),
  });

  const [tableState, setTableState] = useState(nvrList || []);
  const [initTableState, setInitTableState] = useState(nvrList || []);
  const [checkAll, setCheckAll] = useState(false);

  useEffect(() => {
    if (orgUserInfo) {
      onClickInitiateSearchOption();
      fetchUserHead();
    }
  }, [orgUserInfo.userId]);

  const fetchUserHead = async () => {
    const req = { hCd: orgUserInfo.hCd };
    const res = await apiGet({ path: '/user/head', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const findUser = data.userList.find((el: any) => el.userId === orgUserInfo.userId);
      if (findUser) setOrgUserInfo(findUser);
    }
  };

  useEffect(() => {
    // 필터링 기준
    const filterOptions = {
      nName: searchOption.nName,
      sInfra: sInfraSearch[COMCD_INFRA] === 'A' ? '' : sInfraSearch[COMCD_INFRA],
    };
    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = initTableState.filter((item: any) => {
      return item.nName?.toLowerCase()?.includes(filterOptions.nName?.toLowerCase()) && item.infraCd?.includes(filterOptions.sInfra);
    });

    if (filteredArray.length > 0) {
      setTableState(
        arraySortByAscdOrder(
          filteredArray.map((el: any, i: number) => ({ ...el, rowKey: i, sortKey: i })),
          'cdSort'
        )
      );
    } else setTableState([]);
  }, [searchOption.nName, sInfraSearch[COMCD_INFRA], initTableState]);

  useEffect(() => {
    if (nvrList) {
      const array = orgUserInfo.camVList?.split('|').map((nCd: string) => nCd) || [];
      const newCheckList = nvrList.map((el: any, i: number) => {
        const findObj = array.find((nCd: string) => el.nCd === nCd);
        if (findObj) return { ...el, nCdCheck: true };
        return { ...el, nCdCheck: false, rowKey: i };
      });
      setTableState(newCheckList);
      setInitTableState(newCheckList);
    }
  }, [nvrList, orgUserInfo.camVList]);

  const fetchNormalCdN = async () => {
    try {
      const req = { hCd, sCd, subCd: 'N' };
      const res = await apiGet({ path: '/code/pmsNormalTitle', req });
      const addDefaultObject = { type: COMCD_INFRA, [COMCD_INFRA]: 'A', cdName: t('전체') };
      const formatSelectBoxFormList = res.data.data.pmsNormalTitleList.map((el: IComCd) => {
        return { type: COMCD_INFRA, [COMCD_INFRA]: el.subCd, cdName: el.cdName };
      });
      setInfraCdList(formatSelectBoxFormList);
      return [addDefaultObject, ...formatSelectBoxFormList];
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const fetchNvrList = async () => {
    try {
      const req = { hCd };
      const res = await apiGet({ path: '/head/nvr', req });
      const newList = res.data.data.nvrList
        .filter(({ useYn }: { useYn: 'Y' | 'N' }) => useYn === 'Y')
        .map(({ infraName, nName, infraCd, nCd }: { infraName: string; nName: string; infraCd: string; nCd: string }) => ({ infraName, nName, infraCd, nCd }));
      return newList;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickInitiateSearchOption = () => {
    setSearchOption({ nName: '' });
    setSInfraSearch({ type: COMCD_INFRA, [COMCD_INFRA]: 'A', cdName: t('전체') });
    setTableState(initTableState.map((el: any, i: number) => ({ ...el, rowKey: i, sortKey: i })));
    setCheckAll(false);
  };

  const onClickSave = async () => {
    const nvrToString = tableState
      .filter((el: any) => el.nCdCheck)
      .map((item: any) => String(item.nCd).padStart(3, '0'))
      .join('|');
    const { userId, userName, gCd, hpNum, telNum, eMail, loginerrorCount } = orgUserInfo;
    const req = { hCd: orgUserInfo.hCd, sCd: '00000', userId, userName, gCd, hpNum, telNum, eMail, loginerrorCount, camVList: nvrToString };
    const res = await apiPatch({ path: '/user/head', contentType: 'application/json', req });
    const { message, statusCode } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));
    }
  };

  useEffect(() => {
    if (checkAll) {
      const newState = tableState.map((el: any) => ({ ...el, nCdCheck: true }));
      setTableState(newState);
    } else {
      const newState = tableState.map((el: any) => ({ ...el, nCdCheck: false }));
      setTableState(newState);
    }
  }, [checkAll]);

  const handleCheckboxChange = (value: boolean, index: number) => {
    const copyArray = [...tableState];
    const findObj = tableState.find((el: any, i: number) => index === i);
    copyArray[index] = { ...findObj, nCdCheck: !value };
    setTableState(copyArray);
  };

  if (isErrorNormalCdN || isErrorNvrList) return <IssueGuide />;
  if (isLoadingNormalCdN || isFetchingNormalCdN || isLoadingNvrList || isFetchingNvrList)
    return (
      <div className='centered-content' style={{ height: '100%' }}>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );

  return (
    <Root>
      <div className='tableWrapper'>
        <div className='detailInfo'>
          <div className='userSet-data'>
            <span>{orgUserInfo.userId}</span>
            <span>{orgUserInfo.userName}</span>
            <span>{t(orgUserInfo.gName)}</span>
          </div>
        </div>
        <SearchOptions align='left'>
          <div className='inputsWrapper'>
            <div className='secondSearchOption fit-width'>
              <div className='flex-basic textBtnGroup'>
                <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
              </div>
            </div>
            <div className='inputForm-row'>
              <div className='inputForm-col withLabelComCf'>
                <label htmlFor='useYn'>{t('건설업종 구분')}</label>
                <SelectBox options={normalCd} defaultOption={t('전체')} state={sInfraSearch} setState={setSInfraSearch} stateKey={COMCD_INFRA} initiateKey={sInfraSearch[COMCD_INFRA]} />
              </div>
            </div>
            <div className='inputForm-row'>
              <div className='inputForm-col'>
                <Input label='' placeholder={t('NVR 명')} type='text' id='nName' name='nName' state={searchOption} setState={setSearchOption} />
              </div>
            </div>
          </div>
        </SearchOptions>
        <InputTable>
          <div>
            <div className='tableTop' />
          </div>
          <div className='thead'>
            <div className='tr'>
              <div className='trCol6p5 flex-center gap05'>
                <input type='checkbox' checked={checkAll} onChange={() => setCheckAll((prev) => !prev)} />
              </div>
              <div className='trCol2p5 flex-center tableStickyNo'>{t('No')}</div>
              <div className='trCol12 flex-center tableStickyTitle'>{t('건설업종')}</div>
              <div className='trCol11 flex-center content-overflow'>{t('NVR 명')}</div>
            </div>
          </div>
          <div className='table'>
            <div className='tbody'>
              {tableState.length === 0 && <center>{/* <h1>사용자를 선택하세요</h1> */}</center>}
              {tableState?.map((el: any, i: number) => (
                <div className='tr' key={i} role='button' tabIndex={0}>
                  <div className='trCol6p5 flex-center'>
                    <input type='checkbox' checked={el.nCdCheck} onChange={() => handleCheckboxChange(el.nCdCheck, i)} />
                  </div>
                  <div className='trCol2p5 flex-center tableStickyNo'>{i + 1}</div>
                  <div className='trCol12 flex-center tableStickyTitle'>{t(el.infraName)}</div>
                  <div className='trCol11 content-overflow'>{t(el.nName)}</div>
                </div>
              ))}
            </div>
          </div>
        </InputTable>
        <ButtonsWrapper>{(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}</ButtonsWrapper>
      </div>
    </Root>
  );
};

export default UserCctvSetting;
